/**
 * Waiting-room chat message document schemas + paths.
 *
 * Matches the codebase's canonical pattern (see `./healthcheck.ts`): the
 * structural schema with `FirestoreTimestampSchema` lives here so it can be
 * consumed by both SDKs; client / admin specializations swap the timestamp
 * schema for their SDK-native one and wrap the result in a converter.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';
import type {SectionId, SectionWeekId} from '../../section/sectionApi';

/**
 * Stored shape for a chat message. Does NOT include the Firestore document
 * id — see {@link ChatMessageSchema} for the id-bearing variant. `.loose()`
 * so additive schema evolution doesn't drop existing docs.
 */
export const StoredChatMessageSchema = z
  .object({
    /** The uid of the user who sent the message. */
    uid: z.string(),
    /** The sender's display name at send time (denormalized). */
    displayName: z.string(),
    /** The sender's profile photo URL at send time, or null. */
    photoURL: z.string().nullable(),
    /** TipTap document JSON. Opaque object; rendered read-only in the message list. */
    contentTipTapJson: z.record(z.string(), z.unknown()),
    /** Plain-text projection of `contentTipTapJson` — used for audit/search/a11y. */
    contentPlainText: z.string(),
    /** Client-generated idempotency token. Lets the composer dedupe on retry. */
    clientMessageId: z.string(),
    /** Server-assigned send time. Authoritative for ordering. */
    createdAt: FirestoreTimestampSchema,
    /** Server-assigned last-edit time. Absent means never edited. */
    editedAt: FirestoreTimestampSchema.optional(),
    /** Server-assigned soft-delete time. Absent means visible. */
    deletedAt: FirestoreTimestampSchema.optional(),
  })
  .loose();

/** Inferred type for {@link StoredChatMessageSchema}. */
export type StoredChatMessage = z.infer<typeof StoredChatMessageSchema>;

/**
 * View-model for the UI: the stored doc plus the Firestore `doc.id`.
 * The id is not part of the schema — the hook attaches it after read.
 */
export interface ChatMessage extends StoredChatMessage {
  /** Firestore document id for the message. */
  readonly id: string;
}

/** Max plaintext length; mirrored in Firestore rules. */
export const CHAT_MESSAGE_MAX_LENGTH = 4000;

/** How long after send an author may edit their own message. Mirrored in Firestore rules. */
export const CHAT_EDIT_WINDOW_MS = 15 * 60 * 1000;

/** Default max gap between two messages that should group visually. */
export const CHAT_GROUP_MAX_GAP_MS = 5 * 60 * 1000;

// ============================================================================
// Paths
// ============================================================================

/**
 * Chat docs live under each section so reads inherit the section's
 * public-read rules; one doc per week anchors the messages + reactions.
 *
 * @param sectionId - The section id the chat belongs to.
 * @returns The Firestore collection path `sections/{sectionId}/chat`.
 */
export function getChatCollectionPath(sectionId: SectionId | string): string {
  return `sections/${sectionId}/chat`;
}

/**
 * Per-week anchor doc. Messages + reactions hang off this doc so rules and
 * cleanup can target a single week without scanning all history.
 *
 * @param args.sectionId - The section id.
 * @param args.sectionWeekId - The zero-indexed section week.
 * @returns Firestore doc path `sections/{sectionId}/chat/{sectionWeekId}`.
 */
export function getChatWeekDocPath(args: {
  sectionId: SectionId | string;
  sectionWeekId: SectionWeekId | number;
}): string {
  return `${getChatCollectionPath(args.sectionId)}/${String(args.sectionWeekId)}`;
}

/**
 * Messages collection path for a week.
 *
 * @param args.sectionId - The section id.
 * @param args.sectionWeekId - The zero-indexed section week.
 * @returns Firestore collection path ending in `/messages`.
 */
export function getChatMessagesCollectionPath(args: {
  sectionId: SectionId | string;
  sectionWeekId: SectionWeekId | number;
}): string {
  return `${getChatWeekDocPath(args)}/messages`;
}

/**
 * Single message document path.
 *
 * @param args.sectionId - The section id.
 * @param args.sectionWeekId - The zero-indexed section week.
 * @param args.messageId - The Firestore-assigned message id.
 * @returns Firestore doc path to the message.
 */
export function getChatMessageDocPath(args: {
  sectionId: SectionId | string;
  sectionWeekId: SectionWeekId | number;
  messageId: string;
}): string {
  return `${getChatMessagesCollectionPath(args)}/${args.messageId}`;
}
